import React from 'react';
import classnames from 'classnames';
import i18n from 'i18next';
import PropTypes from 'prop-types';

import { Typo } from 'uikit/content';

import useVideoCountdown from '../hooks/useVideoCountdown';

import VideoEnrollButton from './videoEnrollButton';

import styles from '../styles/videoCountdown.module.scss';

const VideoCountdownDays = React.memo(({ days }) => <CountdownItem value={days} unit="days" />);
const VideoCountdownHours = React.memo(({ hours }) => <CountdownItem value={hours} unit="hours" />);
const VideoCountdownMinutes = React.memo(({ minutes }) => (
  <CountdownItem value={minutes} unit="minutes" />
));
const VideoCountdownSeconds = React.memo(({ seconds }) => (
  <CountdownItem value={seconds} unit="seconds" />
));

const CountdownItem = ({ value, unit }) => (
  <div className={styles.countdown_item}>
    <Typo variant="d03">{value}</Typo>
    <Typo variant="b01">{i18n.t(`${unit}-abbrev`, { count: value })}</Typo>
  </div>
);

const VideoCountdown = ({
  hasBorder = false,
  endAt,
  enrollmentStatus,
  isEnrolled,
  isSettingEnrollmentStatus,
  setEnrollmentStatus,
  startAt,
  waitlistSpot,
  waitlistTotal,
}) => {
  const { days, hours, minutes, seconds, status } = useVideoCountdown({
    startDate: startAt,
    endDate: endAt,
  });

  const isLive = status === 'live';
  const containerClasses = classnames(styles.container, hasBorder && styles.hasBorder);

  const renderEnrollButton = () => (
    <VideoEnrollButton
      enrollmentStatus={enrollmentStatus}
      isLive={isLive}
      isSettingEnrollmentStatus={isSettingEnrollmentStatus}
      setEnrollmentStatus={setEnrollmentStatus}
      waitlistSpot={waitlistSpot}
      waitlistTotal={waitlistTotal}
    />
  );

  const renderLiveContent = () => {
    if (!isLive || isEnrolled) return;

    return (
      <div className={containerClasses}>
        <div className={styles.title}>
          <Typo variant="d03" weight="medium">
            {i18n.t('event-is-live')}
          </Typo>
        </div>
        {renderEnrollButton()}
      </div>
    );
  };

  const renderEndedContent = () => {
    if (status !== 'ended') return;

    return (
      <div className={containerClasses}>
        <div className={styles.title}>
          <Typo variant="d03" weight="medium">
            {i18n.t('event-has-ended')}
          </Typo>
        </div>
      </div>
    );
  };

  const renderUpcomingContent = () => {
    if (status !== 'upcoming') return;

    return (
      <div className={containerClasses}>
        <div className={styles.title}>
          <Typo variant="b01" weight="medium">
            {i18n.t('event-starts-in')}
          </Typo>
        </div>
        <div className={styles.countdown}>
          <VideoCountdownDays days={days} />
          <VideoCountdownHours hours={hours} />
          <VideoCountdownMinutes minutes={minutes} />
          <VideoCountdownSeconds seconds={seconds} />
        </div>
        {renderEnrollButton()}
      </div>
    );
  };

  return (
    <>
      {renderLiveContent()}
      {renderEndedContent()}
      {renderUpcomingContent()}
    </>
  );
};

VideoCountdown.propTypes = {
  hasBorder: PropTypes.bool,
  startAt: PropTypes.string.isRequired,
  endAt: PropTypes.string.isRequired,
};

export default VideoCountdown;
